import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import generateAdminTransferTokenMutation from 'shared/graphql/user/mutations/generate-admin-transfer-token.gql';
import PublicEnv from 'shared/utils/public-env';
import { GqlGenerateAdminTransferTokenMutation, GqlGenerateAdminTransferTokenMutationVariables } from 'types/graphql';

import { useStores } from 'src/hooks/use-stores';

const EDITOR_ORIGIN = new URL(PublicEnv.consumerUrl || 'https://dutchie.com').origin;

/** The default CMS model opened when a caller doesn't specify one. */
const DEFAULT_MODEL_ID = 'standalone-page';
/** The default content variant opened when a caller doesn't specify one. */
const DEFAULT_VARIANT_KEY = 'draft';

/** Lifecycle messages the embedded editor posts back to this frame. */
type CmsMessage = {
  type: 'cms:error' | 'cms:navigate' | 'cms:published' | 'cms:ready';
  payload?: Record<string, unknown>;
};

type CmsEditorFrameProps = {
  /** Enterprise whose content is being edited. Used to build the editor URL. */
  enterpriseId: string;
  /**
   * Store cName the editor previews against. The editor resolves a dispensary from
   * this value, so without it the live preview has no store. Appended to the built
   * URL as `&cName=<cName>`; ignored when `editorPath` is supplied.
   */
  cName?: string;
  /**
   * CMS model to edit. Maps directly to the editor route segment
   * (`/cms/<enterpriseId>/<modelId>`). Defaults to `standalone-page`.
   */
  modelId?: string;
  /** Content variant to open (e.g. `draft`). Defaults to `draft`. */
  variantKey?: string;
  /**
   * Escape hatch: a fully-formed editor path (`/cms/...?...`) that overrides the
   * URL built from `enterpriseId`/`modelId`/`variantKey`. Prefer the semantic
   * props; reach for this only for routes this component can't yet express.
   */
  editorPath?: string;
  /** Fired once the editor has loaded and been authenticated. */
  onReady?: () => void;
  /** Fired when the user publishes; payload carries the published variant details. */
  onPublished?: (payload?: Record<string, unknown>) => void;
  /** Fired on an editor-side error; payload carries the error details. */
  onError?: (payload?: Record<string, unknown>) => void;
  /** Fired when the editor requests host navigation (e.g. back to a model list). */
  onNavigate?: (payload?: Record<string, unknown>) => void;
};

/**
 * Drop-in iframe host for the Dutchie CMS drag-and-drop editors.
 *
 * Hand it an `enterpriseId` (plus optional `modelId`/`variantKey`) and it takes
 * care of everything needed to display a working editor: it builds the editor
 * URL, loads it in a fixed-origin iframe, generates a short-lived admin transfer
 * token, and hands that token to the editor over `postMessage` once the editor
 * signals `cms:ready` — so no auth token ever appears in a URL. Editor lifecycle
 * events surface through the `on*` callbacks.
 *
 * The component owns none of its own chrome or routing, so any admin surface can
 * mount it directly to prototype a fuller CMS studio around it:
 *
 * ```tsx
 * <CmsEditorFrame
 *   enterpriseId={enterpriseId}
 *   variantKey='draft'
 *   onPublished={() => showToast('Published!')}
 * />
 * ```
 */
export function CmsEditorFrame({
  enterpriseId,
  cName,
  modelId = DEFAULT_MODEL_ID,
  variantKey = DEFAULT_VARIANT_KEY,
  editorPath,
  onReady,
  onPublished,
  onError,
  onNavigate,
}: CmsEditorFrameProps): JSX.Element {
  const { apolloClient } = useStores();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build the editor URL from the semantic props unless an explicit override is
  // supplied, then ensure embed mode so the editor hides its standalone chrome.
  const iframeSrc = useMemo(() => {
    const cNameParam = cName ? `&cName=${encodeURIComponent(cName)}` : '';
    const path =
      editorPath ??
      `/cms/${encodeURIComponent(enterpriseId)}/${encodeURIComponent(modelId)}?variantKey=${encodeURIComponent(
        variantKey
      )}${cNameParam}`;
    if (path.includes('embed=')) return `${EDITOR_ORIGIN}${path}`;
    return `${EDITOR_ORIGIN}${path}${path.includes('?') ? '&' : '?'}embed=true`;
  }, [editorPath, enterpriseId, cName, modelId, variantKey]);

  useEffect(() => {
    setReady(false);
    setError(null);
  }, [iframeSrc]);

  /** Generates a transfer token and sends it to the iframe via postMessage. */
  const sendAuthToIframe = useCallback(async () => {
    try {
      const { data } = await apolloClient.mutate<
        GqlGenerateAdminTransferTokenMutation,
        GqlGenerateAdminTransferTokenMutationVariables
      >({
        mutation: generateAdminTransferTokenMutation,
      });

      const transferToken = data?.generateAdminTransferToken?.transferToken;
      if (!transferToken) {
        setError('Failed to generate auth token');
        return;
      }

      iframeRef.current?.contentWindow?.postMessage(
        { type: 'cms:auth', payload: { token: transferToken } },
        EDITOR_ORIGIN
      );
      setReady(true);
      onReady?.();
    } catch (e) {
      setError('Failed to authenticate editor');
      console.error(e);
    }
  }, [apolloClient, onReady]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== EDITOR_ORIGIN) return;
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) return;

      const msg = event.data as CmsMessage | null;
      if (typeof msg?.type !== 'string' || !msg.type.startsWith('cms:')) return;

      switch (msg.type) {
        case 'cms:ready':
          void sendAuthToIframe();
          break;
        case 'cms:published':
          onPublished?.(msg.payload);
          break;
        case 'cms:error':
          onError?.(msg.payload);
          break;
        case 'cms:navigate':
          onNavigate?.(msg.payload);
          break;
      }
    },
    [sendAuthToIframe, onPublished, onError, onNavigate]
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  if (error) {
    return <ErrorState>{error}</ErrorState>;
  }

  return (
    <FrameContainer>
      {!ready && <LoadingOverlay>Loading editor...</LoadingOverlay>}
      <StyledIframe ref={iframeRef} src={iframeSrc} title='CMS Editor' allow='clipboard-write' />
    </FrameContainer>
  );
}

const FrameContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 64px);
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  font-size: 14px;
  color: #646d72;
  z-index: 1;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 64px);
  border: none;
`;

const ErrorState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 64px);
  font-size: 14px;
  color: #d32f2f;
`;
